import { useCallback, useEffect, useState } from "react";
import type {
  CraftableWeapon,
  CraftablesIndex,
  MaterialDefinition,
  WeaponConfiguration,
} from "@/lib/crafting/types";
import {
  fetchCatalogue,
  fetchSession,
  fetchSharedPlan,
  fetchWeapon,
  type LoadResult,
  type SessionInfo,
} from "@/lib/manifest/client";
import { decodePlan, sharedPlanSchema, type SharedPlan } from "@/lib/crafting/plan";
import { CATALOGUE_TAB_PATHS, useRoute } from "@/lib/router";
import { CraftingScreen } from "@/ui/CraftingScreen";
import { CatalogueScreen } from "@/ui/CatalogueScreen";
import { SiteCredit } from "@/ui/SiteCredit";
import { AccountBadge } from "@/ui/AccountBadge";
import { HomeScreen } from "@/ui/HomeScreen";
import type { CraftedWeaponInstance } from "@/lib/manifest/client";
import { configurationFromInstance } from "@/lib/crafting/engine";
import { LoadingState, MessageState } from "@/ui/states";

/**
 * Route shell.
 *
 *   /                    catalogue
 *   /weapon/:recipeHash  crafting planner
 *   /plan/:encoded       a stateless shared plan, opened in the planner
 *   /shared/:id          a stored shared plan, credited to whoever made it
 *
 * The catalogue index is fetched once and reused: it carries the material and
 * stat-name lookups every weapon page needs, so loading it up front avoids a
 * second round trip on each weapon.
 */
/** The callback redirects back with ?auth=…; turn that into something readable. */
const AUTH_MESSAGES: Record<string, string> = {
  ok: "Signed in with Bungie.",
  "bad-state": "That sign-in link expired or did not match. Please try again.",
  "missing-code": "Bungie did not send a sign-in code. Please try again.",
};

export function App() {
  const { route, navigate } = useRoute();
  const [index, setIndex] = useState<LoadResult<CraftablesIndex> | null>(null);
  const [session, setSession] = useState<SessionInfo>({ signedIn: false });
  const [authNotice, setAuthNotice] = useState<string | null>(null);
  /** The owned weapon the planner should open against, if any. Kept in memory
   *  because an item instance id is private and must never reach the URL. */
  const [ownedWeapon, setOwnedWeapon] = useState<{
    instance: CraftedWeaponInstance;
    recipeHash: number;
  } | null>(null);

  const refreshSession = useCallback(() => {
    void fetchSession().then(setSession);
  }, []);

  useEffect(() => {
    let cancelled = false;
    void fetchCatalogue().then((result) => {
      if (!cancelled) setIndex(result);
    });
    // Signed-out is the default and is not an error, so this never blocks render.
    void fetchSession().then((s) => {
      if (!cancelled) setSession(s);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Surface the OAuth result, then strip the parameter so a refresh does not
  // replay the message or leave it in a copied URL.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const auth = params.get("auth");
    if (!auth) return;
    setAuthNotice(AUTH_MESSAGES[auth] ?? "Sign-in did not complete.");
    params.delete("auth");
    const query = params.toString();
    window.history.replaceState(
      null,
      "",
      window.location.pathname + (query ? `?${query}` : "")
    );
    const timer = setTimeout(() => setAuthNotice(null), 6000);
    return () => clearTimeout(timer);
  }, []);

  /**
   * Opening a weapon from anywhere other than the owned list clears the owned
   * instance. Without this, shaping a new copy of a weapon you already own
   * inherited the owned one's level and equipped perks — the planner would show
   * a fresh shape as though it were already levelled.
   */
  const openWeapon = useCallback(
    (recipeHash: number) => {
      setOwnedWeapon(null);
      navigate(`/weapon/${recipeHash}`);
    },
    [navigate]
  );
  const goHome = useCallback(() => {
    setOwnedWeapon(null);
    navigate("/");
  }, [navigate]);

  /** Held in memory, never in the URL: an item instance id is a private
   *  identifier and must not end up in a shareable link. */
  const openCrafted = useCallback(
    (instance: CraftedWeaponInstance, recipeHash: number) => {
      setOwnedWeapon({ instance, recipeHash });
      navigate(`/weapon/${recipeHash}`);
    },
    [navigate]
  );

  if (index === null)
    return (
      <>
        <SiteCredit />
        <LoadingState label="Loading the weapon catalogue…" />
      </>
    );

  return (
    <>
      <SiteCredit>
        <AccountBadge session={session} onSignedOut={refreshSession} />
      </SiteCredit>
      {authNotice && (
        <p
          role="status"
          className="fixed top-9 right-0 z-40 max-w-xs border-b border-l border-line-dim bg-void/90 px-3 py-2 text-xs text-ink-2 backdrop-blur-sm"
        >
          {authNotice}
        </p>
      )}
      {renderRoute()}
    </>
  );

  function renderRoute() {
    if (index === null) return null;

    if (index.status === "unavailable") {
      return (
        <MessageState
          title="Manifest not published"
          message={index.message}
          hint={index.hint}
          action={{ label: "Retry", onClick: () => window.location.reload() }}
        />
      );
    }

    if (index.status === "error") {
      return (
        <MessageState
          tone="error"
          title="Could not load the catalogue"
          message={index.message}
          action={{ label: "Retry", onClick: () => window.location.reload() }}
        />
      );
    }

    const catalogue = index.data;

    switch (route.name) {
      case "home":
        return (
          <HomeScreen
            catalogue={catalogue}
            session={session}
            onShapeNew={() => navigate("/shape")}
            onBrowse={(tab) => navigate(CATALOGUE_TAB_PATHS[tab])}
          />
        );

      case "catalogue":
        return (
          <CatalogueScreen
            tab={route.tab}
            catalogue={catalogue}
            session={session}
            onTabChange={(t) => navigate(CATALOGUE_TAB_PATHS[t])}
            onBack={goHome}
            onOpenWeapon={openWeapon}
            onOpenCrafted={openCrafted}
          />
        );

      case "weapon": {
        // Only apply the owned instance if it is this weapon — navigating
        // elsewhere must not drag the previous weapon's real configuration along.
        const owned =
          ownedWeapon?.recipeHash === route.recipeHash ? ownedWeapon.instance : undefined;
        return (
          <WeaponRoute
            recipeHash={route.recipeHash}
            catalogue={catalogue}
            onBack={goHome}
            session={session}
            ownedInstance={owned}
          />
        );
      }

      case "plan":
        return (
          <PlanRoute
            encoded={route.encoded}
            catalogue={catalogue}
            onBack={goHome}
            session={session}
          />
        );

      case "shared":
        return (
          <SharedPlanRoute
            id={route.id}
            catalogue={catalogue}
            onBack={goHome}
            session={session}
          />
        );

      default:
        return (
          <MessageState
            title="Page not found"
            message="That link does not point anywhere in this app."
            action={{ label: "Back to catalogue", onClick: goHome }}
          />
        );
    }
  }
}

/** Loads one weapon's full crafting data and hands it to the planner. */
function WeaponRoute({
  recipeHash,
  catalogue,
  onBack,
  session,
  initialPlan,
  baseConfiguration,
  currentWeaponLevel,
  currentLevelPercent,
  notice,
  ownedInstance,
}: {
  recipeHash: number;
  catalogue: CraftablesIndex;
  onBack: () => void;
  session: SessionInfo;
  initialPlan?: WeaponConfiguration;
  baseConfiguration?: WeaponConfiguration;
  currentWeaponLevel?: number;
  currentLevelPercent?: number;
  notice?: string;
  /** A crafted weapon the user owns; puts the planner into reshape mode. */
  ownedInstance?: CraftedWeaponInstance;
}) {
  const [result, setResult] = useState<LoadResult<CraftableWeapon> | null>(null);

  useEffect(() => {
    let cancelled = false;
    setResult(null);
    void fetchWeapon(recipeHash).then((r) => {
      if (!cancelled) setResult(r);
    });
    return () => {
      cancelled = true;
    };
  }, [recipeHash]);

  if (result === null) return <LoadingState label="Loading weapon…" />;

  if (result.status !== "ok") {
    return (
      <MessageState
        tone={result.status === "error" ? "error" : "neutral"}
        title="Weapon unavailable"
        message={result.message}
        hint={result.status === "unavailable" ? result.hint : undefined}
        action={{ label: "Back to catalogue", onClick: onBack }}
      />
    );
  }

  // An owned weapon's real configuration is derived from the plugs actually
  // inserted on it, and takes precedence over anything a link supplied.
  const ownedConfiguration = ownedInstance
    ? configurationFromInstance(result.data, ownedInstance.sockets)
    : undefined;
  const base = ownedConfiguration ?? baseConfiguration;

  return (
    // Keyed by weapon: the planner's state is lazily initialised from the
    // weapon, so without this, navigating A -> B would keep A's plan.
    <CraftingScreen
      key={result.data.recipeHash}
      weapon={result.data}
      materials={catalogue.materials as Record<string, MaterialDefinition>}
      statNames={catalogue.statNames}
      mode={base ? "owned-reshape" : "new-shape"}
      currentConfiguration={base}
      initialPlan={initialPlan}
      currentWeaponLevel={ownedInstance?.weaponLevel ?? currentWeaponLevel}
      currentLevelPercent={currentLevelPercent}
      onBack={onBack}
      notice={notice}
      session={session}
      ownedInstanceId={ownedInstance?.itemInstanceId}
    />
  );
}

/** Opens a shared plan URL. A bad link is a normal thing to receive, so it gets
 *  a recoverable state rather than a crash. */
function PlanRoute({
  encoded,
  catalogue,
  onBack,
  session,
}: {
  encoded: string;
  catalogue: CraftablesIndex;
  onBack: () => void;
  session: SessionInfo;
}) {
  const decoded = decodePlan(encoded);

  if (!decoded.ok) {
    return (
      <MessageState
        tone="error"
        title="That plan link is not valid"
        message={
          decoded.error === "malformed"
            ? "The link looks damaged — it may have been cut short when it was copied."
            : "The link does not describe a plan this version understands."
        }
        action={{ label: "Back to catalogue", onClick: onBack }}
      />
    );
  }

  const { plan, outdated } = decoded;

  return (
    <PlannerFromPlan
      plan={plan}
      catalogue={catalogue}
      onBack={onBack}
      session={session}
      notice={
        outdated
          ? "This plan was made with an older version of the app — some components may have changed."
          : undefined
      }
    />
  );
}

/**
 * A stored shared plan. Unlike `/plan/:encoded`, this one is fetched, which lets
 * it name whoever shared it.
 */
function SharedPlanRoute({
  id,
  catalogue,
  onBack,
  session,
}: {
  id: string;
  catalogue: CraftablesIndex;
  onBack: () => void;
  session: SessionInfo;
}) {
  const [result, setResult] = useState<Awaited<ReturnType<typeof fetchSharedPlan>> | null>(null);

  useEffect(() => {
    let cancelled = false;
    setResult(null);
    void fetchSharedPlan(id).then((r) => {
      if (!cancelled) setResult(r);
    });
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (result === null) return <LoadingState label="Loading shared plan…" />;

  if (result.status !== "ok") {
    return (
      <MessageState
        tone="error"
        title="Shared plan unavailable"
        message={result.message}
        action={{ label: "Back to catalogue", onClick: onBack }}
      />
    );
  }

  // The payload is re-validated on the way in: it came from the network, and the
  // stored schema may predate the current one.
  const parsed = sharedPlanSchema.safeParse(result.data.plan);
  if (!parsed.success) {
    return (
      <MessageState
        tone="error"
        title="Shared plan unavailable"
        message="That plan was made with an incompatible version of the app."
        action={{ label: "Back to catalogue", onClick: onBack }}
      />
    );
  }

  const sharedBy = result.data.sharedBy?.trim();

  return (
    <PlannerFromPlan
      plan={parsed.data}
      catalogue={catalogue}
      onBack={onBack}
      session={session}
      notice={sharedBy ? `Shared by ${sharedBy}` : "Shared plan"}
    />
  );
}

/** Opens the planner on a decoded plan, whichever route supplied it. */
function PlannerFromPlan({
  plan,
  catalogue,
  onBack,
  session,
  notice,
}: {
  plan: SharedPlan;
  catalogue: CraftablesIndex;
  onBack: () => void;
  session: SessionInfo;
  notice?: string;
}) {
  return (
    <WeaponRoute
      recipeHash={plan.w}
      catalogue={catalogue}
      onBack={onBack}
      session={session}
      initialPlan={plan.p}
      baseConfiguration={plan.mode === "owned-reshape" ? plan.b : undefined}
      currentWeaponLevel={plan.lvl}
      currentLevelPercent={plan.pct}
      notice={notice}
    />
  );
}

